import React from 'react';
import { ArrowUpRight, PlayCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import AnimatedBackground from './AnimatedBackground';

const Hero = () => {
  const { content } = useSite();
  const { t, lang } = useLang();
  const heroData = content?.hero || {};
  const techList = t('hero.techCompliance') || [];
  const P = (v) => pickI18n(v, lang);
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="inicio"
      className="relative min-h-screen w-full overflow-hidden bg-[#0d1b2a] flex items-center"
    >
      {/* Background video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        poster={heroData.fallbackImage}
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      >
        <source src={heroData.videoUrl} type="video/mp4" />
      </video>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0d1b2a]/95 via-[#0d1b2a]/70 to-[#0d1b2a]" />
      <AnimatedBackground variant="mesh" intensity={0.7} />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,224,199,0.18),transparent_55%)]" />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27 viewBox=%270 0 120 120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/%3E%3C/svg%3E")'
      }} />

      {/* Grid lines */}
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
        backgroundSize: '80px 80px'
      }} />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 w-full pt-28 sm:pt-32 pb-16 sm:pb-20">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00e0c7]/30 bg-[#00e0c7]/5 backdrop-blur-sm mb-8">
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00e0c7] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00e0c7]"></span>
            </span>
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#9be8dc] font-medium">
              {P(heroData.badge)}
            </span>
          </div>

          <h1 className="font-display text-white text-[34px] sm:text-[48px] md:text-[58px] lg:text-[80px] tracking-tight font-medium" style={{ lineHeight: 1.1 }}>
            {P(heroData.title)}
            <br />
            <span className="bg-gradient-to-r from-[#00e0c7] via-[#5eead4] to-[#67e8f9] bg-clip-text text-transparent italic font-light">
              {P(heroData.titleAccent)}
            </span>
          </h1>

          <p className="mt-6 sm:mt-8 max-w-2xl text-[15px] sm:text-[17px] lg:text-[19px] text-white/65 leading-relaxed">
            {P(heroData.subtitle)}
          </p>

          <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
            <Button
              onClick={() => scrollTo('#contacto')}
              className="group h-12 sm:h-14 px-5 sm:px-7 rounded-full bg-[#00e0c7] hover:bg-[#00c4ad] text-[#0d1b2a] font-semibold text-[14px] sm:text-[15px] shadow-[0_10px_40px_-10px_rgba(0,224,199,0.6)] transition-all"
            >
              {P(heroData.primaryCta)}
              <ArrowUpRight className="w-5 h-5 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Button>
            <Button
              variant="outline"
              onClick={() => scrollTo('#servicios')}
              className="h-12 sm:h-14 px-5 sm:px-7 rounded-full border-white/25 bg-white/5 hover:bg-white/10 text-white hover:text-white font-medium text-[14px] sm:text-[15px] backdrop-blur-sm"
            >
              <PlayCircle className="w-5 h-5 mr-2" />
              {P(heroData.secondaryCta)}
            </Button>
          </div>

          {/* Tech bar */}
          <div className="mt-12 sm:mt-16 flex flex-wrap items-center gap-x-6 sm:gap-x-10 gap-y-2 sm:gap-y-3 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-white/40">
            {techList.map((item, i) => (
              <React.Fragment key={i}>
                <span>{item}</span>
                {i < techList.length - 1 && <span className="w-1 h-1 bg-white/30 rounded-full" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator - right edge */}
      <div className="absolute bottom-10 right-10 hidden lg:flex flex-col items-center gap-2 z-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 [writing-mode:vertical-rl] rotate-180">Scroll</div>
        <div className="w-px h-12 bg-gradient-to-b from-[#00e0c7] to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
